import React, { useState, useEffect } from 'react';
import {
    Box, Typography, TextField, List, ListItemAvatar, Avatar,
    ListItemText, Checkbox, Button, ListItemButton, Grid2
} from '@mui/material';
import { DrawerHeader } from '../drawer/header.drawer';
import { StandardButton } from '../button/primary.button';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { StandardTextValidator } from '../textValidator/standardTextValidator';

export function ChatGroupManage(params: {
    users: any,
    currentUser: any,
    group: any,
    onClose: () => void,
    onCreate: (group: any) => void,
    onUpdate: (group: any) => void,
    onDelete: (group: any) => void
}) {

    const { users, currentUser, group, onClose, onCreate, onUpdate, onDelete } = params;

    //Start with group or empty for new
    const [groupName, setGroupName] = useState(group?.name || '');
    //NOTE: use member.userId here (not member.id, which is the membership id)
    const [selectedUserIds, setSelectedUserIds] = useState(group
        ? group.members?.map((m: any) => m.userId) || []
        : [currentUser.id]);

    useEffect(() => {
        setGroupName(group?.name || '');
        setSelectedUserIds(group ? group.members?.map((m: any) => m.userId) || [] : [currentUser.id]);
    }, [group, currentUser.id]);

    const handleToggle = (userId: any) => {
        setSelectedUserIds((_userId: any) =>
            _userId.includes(userId)
                ? _userId.filter((id: any) => id !== userId)
                : [..._userId, userId]
        );
    };

    const handleSubmit = (e: any) => {
        e.preventDefault();

        //Map existing membership id by userId so we preserve membership ids during update
        const existingByUserId = new Map(
            (group?.members || []).map((m: any) => [m.userId, m.id]) //m.id is membership id from backend VM
        );

        const members = users
            .filter((u: any) => selectedUserIds.includes(u.id))
            .map((u: any) => ({
                id: existingByUserId.get(u.id) ?? 0, //0 => new member
                userId: u.id,
                name: u.name
            }));

        const newGroup = {
            ...group,
            name: groupName,
            members
        };

        if (!group && onCreate)
            onCreate(newGroup);

        if (group && onUpdate)
            onUpdate(newGroup);
    };

    return (
        <Box display="flex" flexDirection="column" height="100%">

            <DrawerHeader name={group ? 'Manage Group' : 'Create Group'} onClose={onClose} />

            <Box sx={{ p: 2, flex: 1, overflowY: 'auto' }}>
                <ValidatorForm
                    onSubmit={handleSubmit}
                    noValidate>
                    <Grid2 size={{ xs: 12 }}>
                        <StandardTextValidator
                            key="name"
                            name="name"
                            label="Name"
                            required
                            fullWidth
                            validators={['required', 'minStringLength:1']}
                            errorMessages={['required field', "Field cannot be empty"]}
                            value={groupName}
                            onChange={(e: any) => setGroupName(e.target.value)}
                        />
                    </Grid2>

                    <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Group Members</Typography>

                    <List dense>
                        {users.map((_user: any) => (
                            <ListItemButton
                                key={_user.id}
                                onClick={() => handleToggle(_user.id)}
                                disabled={_user.id === currentUser.id}
                            >
                                <ListItemAvatar>
                                    <Avatar>{_user.name[0]}</Avatar>
                                </ListItemAvatar>
                                <ListItemText primary={_user.name} />
                                <Checkbox
                                    checked={selectedUserIds.includes(_user.id)}
                                    tabIndex={-1}
                                    disableRipple
                                    onClick={e => e.stopPropagation()}
                                    onChange={() => handleToggle(_user.id)}
                                    disabled={_user.id === currentUser.id}
                                />
                            </ListItemButton>
                        ))}
                    </List>

                    <Grid2 size={{ xs: 12 }}>
                        <Grid2 container spacing={1}>
                            <Grid2 size={{ xs: group == null ? 12 : 6 }}>
                                <StandardButton
                                    disabled={!groupName.trim() || selectedUserIds.length < 2}
                                    icon="save"
                                    type="submit">
                                    {group ? 'Save' : 'Create'}
                                </StandardButton>
                            </Grid2>

                            {(group && onDelete) &&
                                <Grid2 size={{ xs: 6 }}>
                                    <StandardButton
                                        icon="block"
                                        variant="error"
                                        onClick={() => onDelete(group.id)}>
                                        Delete
                                    </StandardButton>
                                </Grid2>
                            }
                        </Grid2>
                    </Grid2>
                </ValidatorForm>
            </Box>
        </Box>
    );
}
